import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  CookieConsentService,
  CookiePreferences,
  ConsentState,
} from '../../../core/services/cookie-consent.service';

@Component({
  selector: 'app-cookie-consent-banner',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cookie-consent-banner.component.html',
  styleUrls: ['./cookie-consent-banner.component.scss'],
})
export class CookieConsentBannerComponent implements OnInit, OnDestroy {
  showBanner = false;
  showDetails = false;
  preferences: CookiePreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  };

  private subscription?: Subscription;

  constructor(private cookieService: CookieConsentService) {}

  ngOnInit(): void {
    // Subscribe to consent state changes
    this.subscription = this.cookieService.consent$.subscribe((state: ConsentState) => {
      this.showBanner = !state.hasConsented;
      if (state.hasConsented) {
        this.preferences = { ...state.preferences };
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.cookieService.acceptAll();
    this.closeBanner();
  }

  /**
   * Accept only necessary cookies
   */
  acceptNecessaryOnly(): void {
    this.cookieService.acceptNecessaryOnly();
    this.closeBanner();
  }

  /**
   * Toggle detailed preferences view
   */
  toggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  /**
   * Save custom preferences
   */
  savePreferences(): void {
    this.cookieService.saveCustomPreferences(this.preferences);
    this.closeBanner();
  }

  /**
   * Close the banner
   */
  closeBanner(): void {
    this.showBanner = false;
    this.showDetails = false;
  }

  /**
   * Get description for cookie type
   */
  getCookieDescription(type: keyof CookiePreferences): string {
    const descriptions = {
      necessary: 'Essential for the website to function properly. These cannot be disabled.',
      analytics:
        'Help us understand how visitors interact with our website by collecting anonymous information.',
      marketing: 'Used to track visitors across websites to display relevant advertisements.',
      functional: 'Enable enhanced functionality like chat widgets and personalized content.',
    };
    return descriptions[type];
  }

  /**
   * Get cookie examples
   */
  getCookieExamples(type: keyof CookiePreferences): string {
    const examples = {
      necessary: 'Session cookies, security tokens, load balancing',
      analytics: 'Google Analytics, page views, user behavior',
      marketing: 'Facebook Pixel, Google Ads, retargeting',
      functional: 'Language preferences, chat widgets, video players',
    };
    return examples[type];
  }
}
